import { useMemo } from 'react';

import qs from 'qs';

import type { UtilityContext } from '@change-corgi/core/react/utilityContext';
import { useUtilityContext } from '@change-corgi/core/react/utilityContext';

import { useCurrentUserIdAsync, useSessionUuidAsync } from 'src/app/shared/hooks/session';
import { useShortUrls as useShortUrlsBase } from 'src/app/shared/hooks/shortUrls';
import { isLoaded } from 'src/app/shared/utils/async';

function getShareUrl(
	{
		sessionUuid,
		currentUserId,
		slug,
		socialNetworkName,
	}: {
		sessionUuid: string | undefined;
		currentUserId: string | undefined;
		slug: string;
		socialNetworkName: string;
	},
	{ resolveUrl }: UtilityContext,
) {
	const recruiterTrackParams = !currentUserId ? {} : { recruiter: currentUserId };
	const recruitedByTrackParams = !sessionUuid ? {} : { recruited_by_id: sessionUuid };
	const trackParams = qs.stringify({
		...recruiterTrackParams,
		...recruitedByTrackParams,
		utm_source: 'movement_share',
		utm_medium: socialNetworkName,
		utm_campaign: socialNetworkName,
	});
	return resolveUrl(`/m/${slug}?${trackParams}`);
}

type Social = 'facebook' | 'twitter' | 'email' | 'whatsapp';

export function useShareUrl({ slug }: { slug: string }): Record<Social, string> {
	const utilityContext = useUtilityContext();
	const sessionUuid = useSessionUuidAsync();
	const currentUserId = useCurrentUserIdAsync();

	const urlArgs = useMemo(
		() => ({
			sessionUuid: isLoaded(sessionUuid) ? sessionUuid.value : undefined,
			currentUserId: isLoaded(currentUserId) ? currentUserId.value : undefined,
			slug,
		}),
		[sessionUuid, currentUserId, slug],
	);

	const loaded = isLoaded(sessionUuid) && isLoaded(currentUserId);

	const urls: Record<Social, string> = useMemo(
		() => ({
			facebook: getShareUrl({ ...urlArgs, socialNetworkName: 'facebook' }, utilityContext),
			twitter: getShareUrl({ ...urlArgs, socialNetworkName: 'twitter' }, utilityContext),
			email: getShareUrl({ ...urlArgs, socialNetworkName: 'email' }, utilityContext),
			whatsapp: getShareUrl({ ...urlArgs, socialNetworkName: 'whatsapp' }, utilityContext),
		}),
		[urlArgs, utilityContext],
	);

	const shortUrls: Partial<Record<Social, string>> = useShortUrlsBase(loaded ? urls : {});

	return useMemo(() => ({ ...urls, ...shortUrls }), [urls, shortUrls]);
}
